import React, { useState, useEffect, useContext } from 'react'
import useTranslate from '../hooks/useTranslate';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCheckCircle, FaCity, FaMap, FaSeedling, FaVectorSquare } from "react-icons/fa";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SummaryApi from '../common';
import { toast } from 'react-toastify';
import service from '../common/HttpService';
import { useDispatch } from 'react-redux';
import Context from '../context';
import { viewUserProfile } from '../store/userSlice';
import { setCookie, getCookie } from '../common/cookieHelper';
import { fetchCropsList } from '../helpers/cropHelper';
import { trackLead } from '../helpers/analyticsHelper';

const SignUp = () => {
    const location = useLocation();
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { fetchUserDetails, fetchUserAddToCart } = useContext(Context)
    const t = useTranslate({
        startJourney: "Start your",
        farmingJourney: "Farming Journey",
        withUs: "with us.",
        joinNetwork: "Join India's fastest growing agricultural network. Get the best expert advice and premium products delivered.",
        verifyJoin: "Verify & Join",
        simpleProcess: "Simple registration process for real farmers",
        farmerRegistration: "FARMER REGISTRATION",
        provideDetails: "Please provide your details for account approval",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        optional: "Optional",
        emailPlaceholder: "Enter email address (optional)",
        mobileNo: "Mobile No.",
        village: "Village",
        yourVillage: "Your Village",
        city: "City",
        state: "State",
        pincode: "Pincode",
        pinPlaceholder: "Pin",
        mainCropId: "Primary Crop",
        selectCrop: "Select Crop",
        landSize: "Land Size (Acres)",
        acresPlaceholder: "Acres",
        registerBtn: "Register as Farmer",
        alreadyRegistered: "Already registered?",
        loginHere: "Login here",
        validMobileErr: "Please enter a valid 10-digit mobile number",
        selectCropErr: "Please select your primary crop",
        registrationSuccess: "Registration Successful!",
        registrationFailed: "Registration failed",
        networkError: "Network Error. Please try again.",
        brandFirst: "Fasal",
        brandSecond: "Shop"
    });

    const [crops, setCrops] = useState([]);
    const [loadingCrops, setLoadingCrops] = useState(false);

    const [data, setData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        village: "",
        crop_id: "",
        pincode: "",
        state: "",
        city: "",
        acrs: "",
    })

    useEffect(() => {
        let isMounted = true;
        const loadCrops = async () => {
            setLoadingCrops(true);
            const list = await fetchCropsList();
            if (isMounted && list && list.length > 0) {
                setCrops(list);
            }
            if (isMounted) setLoadingCrops(false);
        };
        loadCrops();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (location?.state?.mobile) {
            setData((prev) => ({
                ...prev,
                phone: location.state.mobile
            }))
        }
    }, [location])

    const handleOnChange = (e) => {
        const { name, value } = e.target
        // Prevent non-numeric in phone/pincode/acrs
        if (["phone", "pincode", "acrs"].includes(name) && value !== "" && !/^\d+$/.test(value)) return;

        setData((preve) => {
            return {
                ...preve,
                [name]: value
            }
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (data.phone.length !== 10) {
            toast.error(t.validMobileErr)
            return
        }

        if (!data.crop_id) {
            toast.error(t.selectCropErr)
            return
        }

        // Registration API requires a valid token (user must have verified OTP first)
        const token = getCookie('token');
        if (!token) {
            toast.error("Pehle apna mobile number verify karein (OTP)")
            navigate('/login', { state: { mobile: data.phone } })
            return
        }

        try {
            const dataApi = await service.post(SummaryApi.register.url, data)

            if (dataApi.success === "true" || dataApi.success === true) {
                // Track Lead conversion event for Meta Pixel & Google Analytics
                trackLead({
                    content_name: 'Sign Up Registration',
                    status: 'success',
                    phone: data.phone
                });

                // Using extraData since the API structure sends text within extraData instead of message
                const successMsg = (typeof dataApi.extraData === 'string' ? dataApi.extraData : dataApi.message) || t.registrationSuccess;
                toast.success(successMsg)

                // Auto login logic: store token and user data
                if (dataApi.token) {
                    setCookie('token', dataApi.token, 7)
                    setCookie('user_id', dataApi.user_id, 7)

                    // Also store in localStorage as requested
                    localStorage.setItem('token', dataApi.token)
                    localStorage.setItem('user_id', dataApi.user_id)
                }

                // Get active token (either from response or existing cookies/localStorage)
                const activeToken = dataApi.token || getCookie('token') || localStorage.getItem('token');
                if (activeToken) {
                    try {
                        const userAction = await dispatch(viewUserProfile())
                        if (viewUserProfile.fulfilled.match(userAction)) {
                            const userPayload = userAction.payload;
                            setCookie('user_info', JSON.stringify(userPayload), 7)
                            localStorage.setItem('user_info', JSON.stringify(userPayload))
                        }
                    } catch (err) {
                        console.error("Profile fetch failed after signup", err)
                    }

                    if (typeof fetchUserDetails === 'function') fetchUserDetails();
                    if (typeof fetchUserAddToCart === 'function') fetchUserAddToCart();
                }

                // If registration succeeds but they need approval, they remain unapproved 
                navigate("/")
            } else {
                // If extraData is an object with errors
                if (typeof dataApi.extraData === 'object' && dataApi.extraData !== null) {
                    const firstError = Object.values(dataApi.extraData)[0];
                    toast.error(firstError || t.registrationFailed);
                } else if (typeof dataApi.extraData === 'string') {
                    // Sometimes API returns error directly in extraData as a string
                    toast.error(dataApi.extraData || t.registrationFailed)
                } else {
                    toast.error(dataApi.message || t.registrationFailed)
                }
            }
        } catch (error) {
            console.error(error)
            // Error handling for cases where service.post throws (e.g. 400/500 errors)
            const errorMsg = error.data?.message || error.message || t.networkError;
            toast.error(errorMsg)
        }
    }

    return (
        <section id='signup' className="min-h-screen bg-white flex">
            {/* LEFT SIDE: BRANDING/IMAGE (Desktop Only) */}
            <div className="hidden lg:flex w-1/2 bg-[#143d2c] relative items-center justify-center p-20 overflow-hidden text-left">
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                    <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#00b259] rounded-full blur-[120px]"></div>
                    <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] bg-[#f39200] rounded-full blur-[100px]"></div>
                </div>

                <div className="relative z-10 max-w-lg text-white">
                    <div className='mb-10'>
                        <img
                            src='/fasalshop.png'
                            alt='FasalShop'
                            className='h-20 w-auto object-contain'
                        />
                    </div>

                    <h2 className="text-5xl text-white font-extrabold leading-[1.1] mb-6">
                        {t.startJourney} <span className="text-[#00b259]">{t.farmingJourney}</span> {t.withUs}
                    </h2>
                    <p className="text-white/60 text-lg font-medium leading-relaxed mb-10">
                        {t.joinNetwork}
                    </p>

                    <div className="flex flex-col gap-8 pt-10 border-t border-white/10">
                        <div className="flex items-center gap-5">
                            <div className="w-12 h-12 bg-[#00b259]/20 rounded-2xl flex items-center justify-center text-[#00b259] shrink-0 border border-[#00b259]/30">
                                <FaCheckCircle size={20} />
                            </div>
                            <div>
                                <h4 className="font-black text-white text-lg">{t.verifyJoin}</h4>
                                <p className="text-white/40 text-sm font-bold">{t.simpleProcess}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE: SIGNUP FORM */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-slate-50/50 overflow-y-auto">
                <div className="w-full max-w-2xl bg-white p-8 lg:p-12 rounded-[40px] shadow-2xl shadow-green-900/5 border border-white">
                    <div className="lg:hidden mb-8 flex justify-center">
                        <img
                            src='/fasalshop.png'
                            alt='FasalShop'
                            className='h-16 w-auto object-contain'
                        />
                    </div>

                    <div className="mb-8 text-center lg:text-left">
                        <h3 className="text-2xl font-black text-slate-800 mb-1">{t.farmerRegistration}</h3>
                        <p className="text-slate-400 text-sm font-medium">{t.provideDetails}</p>
                    </div>

                    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
                            {/* Personal Details */}
                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.firstName}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaUser className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.firstName}
                                        name='first_name'
                                        value={data.first_name}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.lastName}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaUser className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.lastName}
                                        name='last_name'
                                        value={data.last_name}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.mobileNo}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaPhone className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder='8888888888'
                                        name='phone'
                                        value={data.phone}
                                        onChange={handleOnChange}
                                        maxLength={10}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">
                                    {t.email} <span className="font-semibold text-slate-300 lowercase text-[9px]">({t.optional})</span>
                                </label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaEnvelope className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='email'
                                        placeholder={t.emailPlaceholder}
                                        name='email'
                                        value={data.email}
                                        onChange={handleOnChange}
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.village}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaMapMarkerAlt className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.yourVillage}
                                        name='village'
                                        value={data.village}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            {/* Address Details */}
                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.city}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaCity className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.city}
                                        name='city'
                                        value={data.city}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.state}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaMap className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.state}
                                        name='state'
                                        value={data.state}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.pincode}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaMapMarkerAlt className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.pinPlaceholder}
                                        name='pincode'
                                        value={data.pincode}
                                        onChange={handleOnChange}
                                        maxLength={6}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>

                            {/* Agriculture Details */}
                            <div className="flex flex-col gap-1.5 text-left">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.mainCropId}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all relative">
                                    <FaSeedling className="text-slate-300 group-focus-within:text-[#00b259] mr-3 shrink-0" size={14} />
                                    <select
                                        name='crop_id'
                                        value={data.crop_id}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold cursor-pointer appearance-none pr-8'
                                    >
                                        <option value="" disabled className="text-slate-400">
                                            {loadingCrops ? "Loading..." : t.selectCrop}
                                        </option>
                                        {crops.map((crop) => (
                                            <option key={crop.id} value={crop.id} className="text-slate-800 font-medium">
                                                {crop.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 pointer-events-none text-slate-400">
                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-1.5 text-left md:col-span-2">
                                <label className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 ml-1">{t.landSize}</label>
                                <div className="group flex items-center bg-slate-100/80 border-2 border-transparent focus-within:border-[#00b259] focus-within:bg-white rounded-[16px] px-5 py-3 transition-all">
                                    <FaVectorSquare className="text-slate-300 group-focus-within:text-[#00b259] mr-3" size={14} />
                                    <input
                                        type='text'
                                        placeholder={t.acresPlaceholder}
                                        name='acrs'
                                        value={data.acrs}
                                        onChange={handleOnChange}
                                        required
                                        className='w-full bg-transparent outline-none text-slate-700 font-bold placeholder:text-slate-300' />
                                </div>
                            </div>
                        </div>

                        <button className='bg-[#143d2c] hover:bg-[#00b259] text-white font-black py-4 rounded-[16px] shadow-xl shadow-green-900/10 hover:shadow-[#00b259]/20 transition-all text-sm uppercase tracking-[2px] mt-4 flex items-center justify-center gap-3 active:scale-95'>
                            {t.registerBtn}
                        </button>
                    </form>

                    <div className="mt-8 border-t border-slate-100 pt-6 text-center">
                        <p className="text-slate-400 font-bold text-sm">
                            {t.alreadyRegistered}
                            <Link to="/login" className="text-[#00b259] ml-2 hover:underline">{t.loginHere}</Link>
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SignUp